import React, { useState } from "react";

import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import {
  CardElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { get, isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";

import SavedCards from "./SavedCards";
import MDButton from "components/MDButton";
import { amountToShow } from "global/functions";
import { postParkingCardPayment } from "store/slice/payment/paymentSlice";
import { postUnitParkingCardPayment } from "store/slice/payment/paymentSlice";
import { updateParkingAfter3dSecure } from "store/slice/payment/paymentSlice";
import { TextField } from "@mui/material";

const Card = (props) => {
  const { shortlyData, formikProps } = props;
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const cardDetails = useSelector((state) => state.payment.cardDetails);
  const [show, setShow] = useState(!isEmpty(cardDetails));
  const [errorAlert, setErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [, setCvcError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    email: "",
  });
  const shortlyId = get(shortlyData, "_id", "");
  const customerId = get(shortlyData, "customerId", "");
  const totalPayAmount = get(shortlyData, "totalAmount", 0);
  const isUnitParkingFlow = get(shortlyData, "isUnitParkingFlow", false);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleChangeCustomerDetail = (e) => {
    const { name, value } = e.target;
    setCustomerDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handlePayment = (stripe, elements) => async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    const cardElement = elements.getElement(CardElement);
    const isInvalid = get(cardElement, "_invalid", false);
    const isEmpty = get(cardElement, "_empty", false);
    const isValid = !(isEmpty || isInvalid);

    const normalizedName = get(customerDetails, "name", "").trim();
    const normalizedEmail = get(customerDetails, "email", "").trim();

    const isNameValid = !!normalizedName;
    const isEmailValid = !!normalizedEmail;
    const isEmailValidFormat = emailRegex.test(normalizedEmail);

    if (!isEmailValid) {
      setErrorAlert(true);
      setErrorMessage("Email is required.");
      return;
    }

    if (!isEmailValidFormat) {
      setErrorAlert(true);
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    const normalizedCustomerDetails = {
      ...customerDetails,
      name: normalizedName,
      email: normalizedEmail,
    };

    const postPayment = async (
      cardId,
      saveLater,
      customerDetails,
      cvcTokenId = null,
    ) => {
      const payload = {
        shortlyId,
        customerId,
        customerDetails,
        paymentMethodId: cardId,
        paymentMethodType: "card",
        saveCard: saveLater,
        cvcToken: cvcTokenId,
      };

      try {
        let result;
        if (isUnitParkingFlow) {
          result = await dispatch(postUnitParkingCardPayment(payload)).unwrap();
        } else {
          result = await dispatch(postParkingCardPayment(payload)).unwrap();
        }

        console.log("result ===>", result);

        if (result.success) {
          if (result.isAlreadyPaid) {
            navigate("/parking/payment/result", {
              state: { status: "alreadyPaid", data: result.data },
            });
            return;
          }
          if (result.requiresAction) {
            const { error, paymentIntent } = await stripe.confirmCardPayment(
              result.clientSecret,
            );

            const securePayload = {
              securePaymentDataId: result.securePaymentDataId,
              paymentIntent,
              error: error?.message,
            };

            const secureResult = await dispatch(
              updateParkingAfter3dSecure(securePayload),
            ).unwrap();

            setLoading(false);
            navigate("/parking/payment/result", {
              state: {
                status: secureResult.success ? "paid" : "failed",
                data: secureResult.data,
              },
            });
          } else {
            setLoading(false);
            navigate("/parking/payment/result", {
              state: {
                status: "paid",
                data: result.data,
              },
            });
          }
        } else {
          navigate("/parking/payment/result", {
            state: { status: "failed", data: result },
          });
        }
      } catch (error) {
        console.error("post payment error ========>", error);
      } finally {
        setLoading(false);
      }
    };

    if (!show) {
      if (isNameValid && isEmailValid) {
        if (isValid) {
          setErrorAlert(false);
          setLoading(true);
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: normalizedCustomerDetails.name,
              email: normalizedCustomerDetails.email,
              phone: shortlyData?.phoneNumber || null,
            },
          });
          if (error) {
            setErrorAlert(true);
            setErrorMessage(error.message);
            setLoading(false);
            console.log("error ===>", error);
          } else {
            postPayment(
              get(paymentMethod, "id", ""),
              get(formikProps, "values.saveLater", false),
              normalizedCustomerDetails,
            );
          }
        } else {
          setErrorAlert(true);
          setErrorMessage("Please enter valid card details.");
        }
      } else {
        setErrorAlert(true);
        setErrorMessage("Name and email are required");
      }
    } else {
      if (isNameValid && isEmailValid) {
        setLoading(true);
        const cvcElement = elements.getElement(CardCvcElement);
        let cvcTokenId = null;

        if (cvcElement) {
          const { token, error } = await stripe.createToken(
            "cvc_update",
            cvcElement,
          );
          if (error) {
            console.error("CVC token error ============>", error);
            setLoading(false);
            setErrorAlert(true);
            setErrorMessage(error.message);
            return;
          }
          cvcTokenId = token?.id || null;
          setCvcError(false);
        }

        postPayment(
          get(formikProps, "values.selectedCard", ""),
          get(formikProps, "values.saveLater", false),
          normalizedCustomerDetails,
          cvcTokenId,
        );
      } else {
        setErrorAlert(true);
        setErrorMessage("Your name and email are required");
      }
    }
  };

  const CARD_ELEMENT_OPTIONS = {
    iconStyle: "solid",
    hidePostalCode: false,
    style: {
      base: {
        iconColor: `${theme.palette.secondary.main}`,
        color: `${theme.palette.secondary.main}`,
        fontSize: "18px",
        fontWeight: 500,
        fontSmoothing: "antialiased",
        ":-webkit-autofill": {
          color: "#fce883",
        },
        "::placeholder": {
          color: `${theme.palette.grey[400]}`,
        },
      },
      invalid: {
        color: `${theme.palette.error.main}`,
        ":focus": {
          color: `${theme.palette.error.light}`,
        },
      },
    },
  };

  const isCustomerDetailsValid = () => {
    const name = get(customerDetails, "name", "").trim();
    const email = get(customerDetails, "email", "").trim();

    return !!name && !!email && emailRegex.test(email);
  };

  return (
    <>
      {show ? (
        <>
          <SavedCards
            customerId={customerId}
            formikProps={formikProps}
            setShow={setShow}
          />
          {errorAlert && (
            <Alert
              severity="error"
              className="mb-2"
              onClose={() => {
                setErrorAlert(false);
              }}
            >
              {errorMessage || "Please First Fill Your Card Details"}
            </Alert>
          )}
          {/* Ask for CVC when charging a saved card */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              width: "100%",
              borderRadius: 2,
              boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              <TextField
                required
                label="Name"
                name="name"
                value={customerDetails.name}
                onChange={handleChangeCustomerDetail}
                variant="outlined"
                size="small"
                fullWidth
              />
              <TextField
                required
                type="email"
                label="Email"
                name="email"
                value={customerDetails.email}
                onChange={handleChangeCustomerDetail}
                onBlur={() => {
                  setCustomerDetails((prev) => ({
                    ...prev,
                    email: prev.email.trim(),
                  }));
                }}
                variant="outlined"
                size="small"
                error={
                  customerDetails.email.length > 0 &&
                  !emailRegex.test(customerDetails.email.trim())
                }
                helperText={
                  customerDetails.email.length > 0 &&
                  !emailRegex.test(customerDetails.email.trim())
                    ? "Enter a valid email address"
                    : ""
                }
                fullWidth
              />
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRadius: 1,
                }}
              >
                <CardCvcElement />
              </Box>
            </Box>
          </Paper>
          <Typography
            variant="subtitle1"
            color="secondary"
            className="my-3 text-center cursor-pointer"
            onClick={() => setShow(!show)}
            fontSize="16px"
          >
            + Add a new Card
          </Typography>
        </>
      ) : (
        <>
          {errorAlert && (
            <Alert
              severity="error"
              className="mb-2"
              onClose={() => {
                setErrorAlert(false);
              }}
            >
              {errorMessage || "Please First Fill Your Card Details"}
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              width: "100%",
              borderRadius: 2,
              boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              <TextField
                required
                label="Name"
                name="name"
                value={customerDetails.name}
                onChange={handleChangeCustomerDetail}
                variant="outlined"
                size="small"
                fullWidth
              />
              <TextField
                required
                label="Email"
                name="email"
                value={customerDetails.email}
                onChange={handleChangeCustomerDetail}
                variant="outlined"
                size="small"
                helperText={
                  customerDetails.email.length > 0 &&
                  !emailRegex.test(customerDetails.email)
                    ? "Enter a valid email address"
                    : ""
                }
                fullWidth
              />
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRadius: 1,
                }}
              >
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </Box>
            </Box>
          </Paper>
          <FormGroup
            className="my-1"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="saveLater"
                  checked={formikProps.values.saveLater}
                  onChange={formikProps.handleChange}
                />
              }
              label="Save Card"
            />
          </FormGroup>
          {!isEmpty(cardDetails) && (
            <Typography
              variant="subtitle1"
              component="p"
              color="secondary"
              className="mb-3 d-flex align-items-center justify-content-center gap-2 cursor-pointer"
              onClick={() => setShow(!show)}
            >
              <RemoveRedEyeIcon /> View All Cards
            </Typography>
          )}
        </>
      )}
      <Box className="text-center">
        <MDButton
          variant="contained"
          color={sidenavColor}
          onClick={handlePayment(stripe, elements)}
          disabled={loading || !isCustomerDetailsValid()}
          sx={{ width: { xs: "150px", sm: "250px" } }}
        >
          {`Pay $${amountToShow(totalPayAmount)}`}
        </MDButton>
      </Box>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Stack direction="column" spacing={1} alignItems="center">
          <CircularProgress color="inherit" />
          <Typography
            variant="h5"
            sx={{ color: "primary.contrastText" }}
            className="mt-4"
          >
            Please wait payment is processing.....
          </Typography>
        </Stack>
      </Backdrop>
    </>
  );
};

export default Card;
